// Constants
import { FileTypeExtensions } from "@ms/uno-constants/lib/local/FileTypeExtensions";
// Models
import { ContentLinkType, DocumentReferenceLegacyType, WebReferenceLegacyType } from "../service/planner/enumerations/IPlannerEnumerations";
// Utilities
import Uri from "@ms/uno-utilities/lib/local/Uri";
/**
 * Reverse map: file extension to file type
 */ const ExtensionToFileType = (()=>{
    const reverseMap = {};
    for (const [fileType, extensions] of Object.entries(FileTypeExtensions)){
        for (const ext of extensions){
            reverseMap[ext] = fileType;
        }
    }
    return reverseMap;
})();
/**
 * Gets the link type and legacy type from a web url.
 * @param webUrl The web URL to check
 */ export function getLinkTypeFromUrl(webUrl) {
    const fileExtension = getFileExtensionFromUrl(webUrl);
    return getLinkTypeFromExtension(fileExtension);
}
/**
 * Gets the file extension from the webUrl.
 * @param webUrl The URL of the file
 */ export function getFileExtensionFromUrl(webUrl) {
    if (!webUrl) {
        return undefined;
    }
    // Decode the URL to handle encoded characters like %2E (.)
    let decodedUrl;
    try {
        decodedUrl = decodeURIComponent(webUrl);
    } catch (e) {
        // Unable to decode URL, return undefined
        return undefined;
    }
    const url = new Uri(decodedUrl);
    const urlScheme = url.getScheme().toLowerCase();
    if (urlScheme !== "http" && urlScheme !== "https") {
        // Unsupported URL scheme, return undefined
        return undefined;
    }
    // We have two patterns for the URL:
    // 1) https://microsoft.sharepoint.com/teams/{groupName}/Shared Documents/{itemPath/fileName}?web=1
    // 2) https://microsoft.sharepoint-df.com/teams/{groupName}/_layouts/15/Doc.aspx?sourcedoc={eTag}&file={fileName}&action=default&mobileredirect=true
    const queryParams = url.getQueryAsObject();
    const lastPathSegment = url.getPathSegments().pop();
    // For format 2, check if the URL matches the Doc.aspx pattern and has required query params
    if (lastPathSegment?.toLowerCase() === "doc.aspx" && queryParams?.file && queryParams?.sourcedoc) {
        return getFileExtensionFromFilename(queryParams.file);
    }
    return getFileExtensionFromFilename(lastPathSegment);
}
/**
 * Gets the file extension from a filename.
 * @param filename The name of the file
 * @returns The file extension (without dot) in lowercase, or undefined if not found
 */ export function getFileExtensionFromFilename(filename) {
    // Check if filename contains a dot (has an extension)
    if (!filename?.includes(".")) {
        return undefined;
    }
    // Extract extension (everything after the last .)
    const extension = filename.split(".").pop()?.toLowerCase();
    return extension;
}
/**
 * Gets the link type and legacy type from a file extension.
 * @param fileExtension The file extension to check
 */ function getLinkTypeFromExtension(fileExtension) {
    if (!fileExtension) {
        return {
            linkType: ContentLinkType.WebReference,
            resourceClassificationType: WebReferenceLegacyType.Other
        };
    }
    const documentLegacyType = getDocumentLegacyReferenceTypeFromExtension(fileExtension);
    if (documentLegacyType) {
        return {
            linkType: ContentLinkType.DocumentReference,
            resourceClassificationType: documentLegacyType
        };
    }
    const resourceClassificationType = getWebLegacyReferenceTypeFromExtension(fileExtension);
    return {
        linkType: ContentLinkType.WebReference,
        resourceClassificationType
    };
}
/**
 * Gets the legacy web reference type from a file extension.
 * @param fileExtension The file extension to check
 */ function getWebLegacyReferenceTypeFromExtension(fileExtension) {
    const extension = fileExtension.toLowerCase();
    const fileType = ExtensionToFileType[extension];
    if (fileType) {
        return WebReferenceLegacyType[fileType] ?? WebReferenceLegacyType.Other;
    }
    return WebReferenceLegacyType.Other;
}
/**
 * Gets the legacy document reference type from a file extension.
 * @param fileExtension The file extension to check
 */ export function getDocumentLegacyReferenceTypeFromExtension(fileExtension) {
    const extension = fileExtension.toLowerCase();
    const fileType = ExtensionToFileType[extension];
    if (!fileType) {
        return undefined;
    }
    return DocumentReferenceLegacyType[fileType] ?? undefined;
}
